import { generateText as geminiGenerateText } from '@/lib/gemini';
import { generateText as anthropicGenerateText } from '@/lib/anthropic';
import { trackUsage } from '@/utils/costTracker';
import { openaiCompatFetch } from '../../utils/openaiProxyFetch';
import { analyzePrompt } from '../SunoReadiness/rulesEngine';
import type { ReadinessInput } from '../SunoReadiness/types';
import type { PromptDoctorResult } from './types';
import {
  getProvider, getGeminiKey, getGeminiModel,
  getClaudeKey, getClaudeProxy, getClaudeModel,
  getOpenAIKey, getOpenAIModel, getXAIKey, getXAIModel,
} from '@/services/llmShared';

const SYSTEM_PROMPT = `You are a Suno V5 prompt repair specialist. You receive a style prompt, its automated readiness score, and the specific issues found by a rules engine. Your job: explain what's wrong in plain English and produce a fixed style string.

Suno is a pattern-matching model, NOT an instruction-follower. It responds to vocabulary it has strong training associations with:
- Genre anchor terms create the strongest signal — put them FIRST
- Behavioral/evocative language (gritty, soaring, haunting) shapes mood and texture
- DAW/engineering terms (reverb tail, sidechain, EQ, LUFS, compression) are dead weight — remove only the ones listed under "Dead-weight terms found" and leave every other term in place
- If the issues name a conflicting genre pair, pick the dominant one and add one complementary descriptor. Genres the issues do not name as conflicting stay, even when there are several.
- Keep every genre the style names, its BPM, and any structure cue (breakdown, drop, ending). Never add a descriptor the original does not carry. Stay under 1000 characters, and never shorten a style just to make it shorter.
- If no real issue is listed, return the original style unchanged as fixedStyle and say so in the diagnosis

Return ONLY valid JSON matching this exact schema:
{
  "diagnosis": "2-3 sentence plain-English explanation of the specific issues found in THIS prompt",
  "fixedStyle": "the rewritten style string, ready to paste into Suno",
  "changes": [
    { "what": "specific change made to the prompt", "why": "why this helps Suno respond better" }
  ]
}`;

function buildUserPrompt(input: ReadinessInput): string {
  const readiness = analyzePrompt(input);

  const issueLines: string[] = [];
  for (const [, cat] of Object.entries(readiness.categories)) {
    if (cat.issues.length > 0) {
      issueLines.push(`  ${cat.label} (${Math.round(cat.score)}/100): ${cat.issues.join('; ')}`);
    }
  }
  if (readiness.deadWeightTermsFound.length > 0) {
    issueLines.push(`  Dead-weight terms found: ${readiness.deadWeightTermsFound.join(', ')}`);
  }
  if (readiness.conflictingGenresFound.length > 0) {
    issueLines.push(`  Conflicting genre pairs: ${readiness.conflictingGenresFound.map(([a, b]) => `${a} + ${b}`).join(', ')}`);
  }
  if (readiness.conflictingVocalsFound.length > 0) {
    issueLines.push(`  Conflicting vocal cues: ${readiness.conflictingVocalsFound.map(([a, b]) => `${a} + ${b}`).join(', ')}`);
  }

  return `Fix this Suno style prompt.

ORIGINAL STYLE: ${input.styleText}
${input.blends.length > 0 ? `GENRE BLENDS: ${input.blends.join(', ')}` : ''}
${input.lyricsText ? `LYRICS EXCERPT: ${input.lyricsText.substring(0, 400)}` : ''}

READINESS SCORE: ${readiness.totalScore}/100 (${readiness.grade.toUpperCase()})
ISSUES FOUND:
${issueLines.length > 0 ? issueLines.join('\n') : '  No critical issues detected — leave the style as it is unless the term order clearly hurts it.'}

Diagnose the issues above and rewrite the style string to fix them.`;
}

function parseResponse(text: string): PromptDoctorResult {
  const cleaned = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
  const match = cleaned.match(/\{[\s\S]*\}/);
  if (!match) throw new Error('Response was cut off. Try shortening your style prompt or switching providers.');
  try {
    return JSON.parse(match[0]) as PromptDoctorResult;
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'unknown';
    throw new Error(`Could not parse response — likely truncated. Try a shorter prompt or switching providers. (${detail})`);
  }
}

async function withGemini(input: ReadinessInput): Promise<PromptDoctorResult> {
  const apiKey = getGeminiKey();
  if (!apiKey) throw new Error('Gemini API key not configured.');
  const model = getGeminiModel();
  const { text, inputTokens, outputTokens } = await geminiGenerateText({
    apiKey, model, prompt: buildUserPrompt(input),
    systemInstruction: SYSTEM_PROMPT, temperature: 0.4, maxOutputTokens: 1500,
  });
  trackUsage({ provider: 'gemini', model, feature: 'prompt-doctor', inputTokens, outputTokens });
  if (!text) throw new Error('Empty response from Gemini.');
  return parseResponse(text);
}

async function withClaude(input: ReadinessInput): Promise<PromptDoctorResult> {
  const apiKey = getClaudeKey();
  if (!apiKey) throw new Error('Claude API key not configured.');
  const model = getClaudeModel();
  const { text, inputTokens, outputTokens } = await anthropicGenerateText({
    apiKey, baseURL: getClaudeProxy(), model,
    systemPrompt: SYSTEM_PROMPT, userPrompt: buildUserPrompt(input),
    temperature: 0.4, maxTokens: 1500,
  });
  trackUsage({ provider: 'claude', model, feature: 'prompt-doctor', inputTokens, outputTokens });
  return parseResponse(text);
}

async function withOpenAI(input: ReadinessInput): Promise<PromptDoctorResult> {
  const apiKey = getOpenAIKey();
  if (!apiKey) throw new Error('OpenAI API key not configured.');
  const model = getOpenAIModel();
  const res = await openaiCompatFetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify({
      model, temperature: 0.4, max_tokens: 1500,
      response_format: { type: 'json_object' },
      messages: [{ role: 'system', content: SYSTEM_PROMPT }, { role: 'user', content: buildUserPrompt(input) }],
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error?.message || 'OpenAI request failed.');
  if (data.usage) trackUsage({ provider: 'openai', model, feature: 'prompt-doctor', inputTokens: data.usage.prompt_tokens || 0, outputTokens: data.usage.completion_tokens || 0 });
  return parseResponse(data.choices[0].message.content);
}

async function withXAI(input: ReadinessInput): Promise<PromptDoctorResult> {
  const apiKey = getXAIKey();
  if (!apiKey) throw new Error('xAI API key not configured.');
  const model = getXAIModel();
  const res = await fetch('https://api.x.ai/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify({
      model, temperature: 0.4, max_tokens: 1500,
      messages: [{ role: 'system', content: SYSTEM_PROMPT }, { role: 'user', content: buildUserPrompt(input) }],
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error?.message || 'xAI request failed.');
  if (data.usage) trackUsage({ provider: 'xai', model, feature: 'prompt-doctor', inputTokens: data.usage.prompt_tokens || 0, outputTokens: data.usage.completion_tokens || 0 });
  return parseResponse(data.choices[0].message.content);
}

export async function runPromptDoctor(input: ReadinessInput): Promise<PromptDoctorResult> {
  const provider = getProvider();
  console.log(`🩺 [PromptDoctor] Running via ${provider}`);
  switch (provider) {
    case 'claude': return withClaude(input);
    case 'openai': return withOpenAI(input);
    case 'xai':    return withXAI(input);
    case 'gemini':
    default:       return withGemini(input);
  }
}
